import { MediaType } from './types';

export type SizeOption = {
  id: string;
  label: string;
  value: string | boolean;
};

export type EmbedQuerystring = {
  experience?: string;
  width?: string;
  height?: string;
  pan?: boolean;
  zoom?: boolean;
  rotateHorizontal?: boolean;
  rotateVertical?: boolean;
  scale?: boolean;
};

// Feature flags that can gate a template. Keyed on the ModeContext flags so adding
// a new gate is: add a key here, tag the template, and pass the flag through.
export type EmbedFeatureFlags = {
  useNestleFeatures?: boolean;
  premium?: boolean;
};

export type EmbedTemplate = {
  label: string;
  value: string;
  mediaTypes?: MediaType[];
  requiredFeatures?: (keyof EmbedFeatureFlags)[];
  buildUrl: (backendUrl: string, queryParams?: EmbedQuerystring) => string;
  embedOptions: SizeOption[];
};

function extractFolder(url: string): string {
  const segments = new URL(url).pathname.split('/').filter(Boolean);
  return segments.slice(0, -1).join('/');
}

function generateQueryStringFromObject(obj: Record<string, string | boolean>): string {
  const params = new URLSearchParams();
  for (const key in obj) {
    params.append(key, String(obj[key]));
  }
  return params.toString();
}

export const EMBED_TEMPLATES: EmbedTemplate[] = [
  {
    label: 'File only',
    value: 'file',
    mediaTypes: Object.values(MediaType).filter(t => t !== MediaType.Model3D),
    buildUrl: (url) => url,
    embedOptions: [
      { id: 'width', label: 'Width', value: '640' },
      { id: 'height', label: 'Height', value: '480' },
    ],
  },
  {
    label: '3D Viewer',
    value: 'basic-3d-viewer',
    mediaTypes: [MediaType.Model3D],
    buildUrl: (url, queryParams) => `https://7lgrv.zappar-us.io/viewer/?key=${extractFolder(url)}&${generateQueryStringFromObject(queryParams ?? {})}`,
    embedOptions: [
      { id: 'width', label: 'Width', value: '640' },
      { id: 'height', label: 'Height', value: '480' },
      { id: 'pan', label: 'Pan', value: true },
      { id: 'zoom', label: 'Zoom', value: true },
      { id: 'rotateHorizontal', label: 'Rotate (horizontal)', value: true },
      { id: 'rotateVertical', label: 'Rotate (vertical)', value: true },
      { id: 'scale', label: 'Scale', value: true },
    ],
  },
  {
    label: 'Nestle – 3D Viewer',
    value: 'nestle-3d-viewer',
    mediaTypes: [MediaType.Model3D],
    requiredFeatures: ['useNestleFeatures'],
    buildUrl: (url, queryParams) => `https://nest-mv.zap.works/?key=${extractFolder(url)}&${generateQueryStringFromObject(queryParams ?? {})}`,
    embedOptions: [
      { id: 'width', label: 'Width', value: '640' },
      { id: 'height', label: 'Height', value: '480' },
      { id: 'pan', label: 'Pan', value: true },
      { id: 'zoom', label: 'Zoom', value: true },
      { id: 'rotateHorizontal', label: 'Rotate (horizontal)', value: true },
      { id: 'rotateVertical', label: 'Rotate (vertical)', value: true },
      { id: 'scale', label: 'Scale', value: true },
    ],
  },
];

// Return the templates visible for the given media type and active feature flags.
// A template is excluded if its mediaTypes don't include `mediaType` (when given),
// or if any of its requiredFeatures flags are not enabled.
export function getEmbedTemplates(
  opts: { mediaType?: MediaType; features?: EmbedFeatureFlags } = {},
): EmbedTemplate[] {
  const { mediaType, features = {} } = opts;
  return EMBED_TEMPLATES.filter(t => {
    if (mediaType && t.mediaTypes && !t.mediaTypes.includes(mediaType)) return false;
    if (t.requiredFeatures?.some(flag => !features[flag])) return false;
    return true;
  });
}
